// @flow
import {type AxiosPromise} from 'axios';
import type {Reducer} from 'redux';
import {createActions, handleActions, type ActionType} from 'redux-actions';
import {FULFILLED, PENDING, REJECTED} from 'redux-promise-middleware';
import {ENDPOINTS} from 'constants/endpoints';
import type {WindowProductType} from 'constants/flow/flowTypesProduct';
import {instanceGusaBackend} from 'libs/api';

export type TryOnProductType = {
    product: WindowProductType,
    status: PENDING | FULFILLED | REJECTED,
};

export const defaultState: TryOnProductType = {
    product: {
        children: [],
        active_tab: '',
        installments_count: 0,
        manufacturer_value: '',
        parent_id: 0,
        parent_name: '',
        required_total: 0,
        tier_prices: {
            price: 0,
            price_qty: 0,
            savePercent: 0,
        },
        type: '',
        is_eligible_for_hto: false,
        preloaded: false,
    },
    status: PENDING,
};

export const {
    tryOn: {fetchProduct, setProduct},
} = createActions({
    TRY_ON: {
        /**
         * Fetch product if it isn't exist
         * @param {number} productId - product ID
         * @return {AxiosPromise} Window product request
         */
        FETCH_PRODUCT: (productId: number): AxiosPromise =>
            instanceGusaBackend.get(`${ENDPOINTS.PRODUCT_GET_V2}/${productId}`),

        /**
         * Handler opening "Try on" popup
         * @param {WindowProductType} product - Window product info
         * @return {WindowProductType} - Window product
         */
        SET_PRODUCT: (product: WindowProductType): WindowProductType => product,
    },
});

export const product: Reducer = handleActions(
    {
        /**
         * Set store info is pending
         * @param {TryOnProductType} state - Current state
         * @param {ActionType<fetchProduct>} action - Dispatched action
         * @return {TryOnProductType} New state
         */
        [`${fetchProduct}_${PENDING}`]: (state: TryOnProductType): TryOnProductType => ({
            ...state,
            status: PENDING,
        }),

        /**
         * Save product info in state
         * @param {TryOnProductType} state - Current state
         * @param {ActionType<fetchProduct>} action - Dispatched action
         * @return {TryOnProductType} New state
         */
        [`${fetchProduct}_${FULFILLED}`]: (
            state: TryOnProductType,
            {payload}: ActionType<typeof fetchProduct>,
        ): TryOnProductType => ({
            ...state,
            product: payload.data,
            status: FULFILLED,
        }),

        /**
         * Save product info in state for "Try on" popup
         * @param {TryOnProductType} state - Current state
         * @param {ActionType<setProduct>} action - Dispatched action
         * @return {TryOnProductType} New state
         */
        [setProduct]: (
            state: TryOnProductType,
            {payload}: ActionType<typeof setProduct>,
        ): TryOnProductType => ({
            ...state,
            product: {...payload},
            status: FULFILLED,
        }),
    },
    defaultState,
);
